// FormikHeadlessListbox.jsx
import React from "react";
import { Field, ErrorMessage } from "formik";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from "@headlessui/react";
import { FaChevronDown } from "react-icons/fa";
import { usePageSettings } from "../../../../contexts/PageSettingsContext";

/**
 * A reusable Listbox (dropdown) powered by Headless UI and integrated with Formik.
 *
 * Props:
 * - label: string to display as field label (or you can use translations)
 * - name: the Formik field name (e.g. "configuration.os.id" or "configuration.paymentMethod")
 * - required: boolean (if you want to show an asterisk or enforce required styles)
 * - options: an array of items, each with { id, name } (or any shape you want).
 * - onChange: callback with signature (newValue, form) called when user picks an item.
 *
 */

const FormikHeadlessListbox = ({ label, name, required = false, options = [], onChange, ...props }) => {
	const { translations } = usePageSettings();

	return (
		<Field name={name}>
			{({ field, form, meta }) => {
				const selectedValue = field.value || ""; // e.g. "paypal"

				// We find the currently selected option object:
				const selectedOption = options.find((o) => o.id === selectedValue);

				// When user picks a new ID from the Listbox:
				const handleChange = (newId) => {
					// Update Formik
					form.setFieldValue(name, newId);

					// Call parent's onChange if provided
					onChange?.(newId, form);
				};

				return (
					<div className="flex flex-col gap-2">
						{/* Field Label */}
						<label htmlFor={name} className="flex items-center gap-1">
							<span className="text-sm uppercase text-neutral-50">{translations[label]}</span>
							{required && (
								<span className="text-ipxon-magenta flex gap-0.5 text-[10px] font-bold uppercase">
									*
								</span>
							)}
						</label>

						{/* The Headless UI Listbox */}
						<Listbox value={selectedValue} onChange={handleChange}>
							{({ open }) => (
								<div className="relative">
									{/* Button: shows the selected item's name (or fallback) */}
									<ListboxButton
										className="
										w-full
										rounded-lg
										border 
										border-neutral-50
										bg-transparent 
										px-3 py-3
										text-neutral-50
										placeholder-neutral-500
										focus:outline-none
										focus:border-white
										focus:ring-0
										focus:ring-offset-0
										flex items-center justify-between
										"
									>
										<span>
											{selectedOption?.name === "No" ? translations["NoOptionSelectedAdditionalIP"] : selectedOption?.name ?? "No option selected"}
										</span>
										<FaChevronDown
											className={`
												text-neutral-50 transition-transform
												${open ? "rotate-0" : "-rotate-90"}
											`}
											size={14}
										/>
									</ListboxButton>

									{/* The dropdown container for options */}
									<Transition
										show={open}
										// Fade in/out transitions
										enter="transition transform ease-out duration-200"
										enterFrom="opacity-0"
										enterTo="opacity-100"
										leave="transition transform ease-in duration-150"
										leaveFrom="opacity-100"
										leaveTo="opacity-0"
										className="absolute z-10 w-full mt-1"
									>
										<ListboxOptions
											static
											className="max-h-60 w-full overflow-auto rounded-lg bg-ipxon-dark-gray border border-neutral-50 text-sm"
										>
											{/* Render real options */}
											{options.map((option) => {
												const displayName = option.name === "No" ? translations["NoOptionSelectedAdditionalIP"] : option.name;
												return (
													<ListboxOption
														key={option.id}
														value={option.id}
														className={({ active, selected }) =>
															`
														cursor-pointer select-none py-3 px-4
														${active ? "bg-neutral-700 text-white" : "text-neutral-300"}
														${selected ? "bg-neutral-600 font-medium" : ""}
														`
														}
													>
														{displayName}
													</ListboxOption>
												);
											})}
										</ListboxOptions>
									</Transition>
								</div>
							)}
						</Listbox>

						{/* Show Formik error if touched + has error */}
						{meta.touched && meta.error && (
							<div className="text-red-500 text-xs">
								<ErrorMessage name={name} />
							</div>
						)}
					</div>
				);
			}}
		</Field>
	);
};

export default FormikHeadlessListbox;

export const FormikHeadlessListboxPayments = ({ label, name, required = true, options = [], onChange, className = "", ...props }) => {
	return (
		<Field name={name}>
			{({ field, form, meta }) => {
				/**
				 * SCENARIO B: Storing entire objects in Formik
				 *
				 * field.value is expected to be an object (e.g., { module: "paypal", name: "PayPal" })
				 */
				const valueObj = field.value || {};
				const selectedValue = valueObj.module || "";

				// Find the currently selected option object:
				const selectedOption = options.find((o) => o.id === selectedValue);

				// When user picks a new module from the Listbox:
				const handleChange = (newId) => {
					if (newId === "") {
						// User selected "No option selected"
						form.setFieldValue(name, null);
						onChange?.(null, form);
					} else {
						// Pass the selected id to the onChange handler
						onChange?.(newId, form);
					}
				};

				return (
					<div className="flex flex-col gap-2">
						{/* Field Label */}
						<label htmlFor={name} className="flex items-center gap-1 ">
							<span className="text-sm uppercase text-neutral-50">{label}</span>

								<span className="text-ipxon-magenta flex gap-0.5 text-[10px] font-bold uppercase">
									*
								</span>

						</label>

						{/* The Headless UI Listbox */}
						<Listbox value={selectedValue} onChange={handleChange}>
							{({ open }) => (
								<div className="relative">
									{/* Button: shows the selected item's name (or fallback) */}
									<ListboxButton
										className={` 
											w-full
											rounded-lg
											border 
											border-neutral-50
											bg-transparent 
											px-4 py-[9px]
											text-neutral-50
											placeholder-neutral-500
											focus:outline-none
											focus:border-white
											focus:ring-0
											focus:ring-offset-0
											flex items-center
											justify-between
											text-xs
											${className} 
										`}
									>
										<span>{selectedOption?.name ?? "No option selected"}</span>
										<FaChevronDown
											className={`
												text-neutral-50 transition-transform
												${open ? "rotate-0" : "-rotate-90"}
											`}
											size={14}
										/>
									</ListboxButton>

									{/* The dropdown container for options */}
									<Transition
										show={open}
										enter="transition transform ease-out duration-200"
										enterFrom="opacity-0"
										enterTo="opacity-100"
										leave="transition transform ease-in duration-150"
										leaveFrom="opacity-100"
										leaveTo="opacity-0"
										className="absolute z-10 w-full mt-1"
									>
										<ListboxOptions
											static
											className="
												max-h-60
												w-full
												overflow-auto
												rounded-lg
												bg-ipxon-dark-gray
												border
												border-neutral-50
												text-sm
											"
										>
											{/* Optional "No option selected" */}
											{!required && (
												<ListboxOption
													value=""
													className={({ active, selected }) =>
														`
														cursor-pointer select-none py-3 px-4
														${active ? "bg-neutral-700 text-white" : "text-neutral-300"}
														${selected ? "bg-neutral-600 font-medium" : ""}
														`
													}
												>
													No option selected
												</ListboxOption>
											)}

											{/* Render your real options */}
											{options.map((option) => (
												<ListboxOption
													key={option.id}
													value={option.id}
													className={({ active, selected }) =>
														`
														cursor-pointer select-none py-3 px-4
														${active ? "bg-neutral-700 text-white" : "text-neutral-300"}
														${selected ? "bg-neutral-600 font-medium" : ""}
														`
													}
												>
													{option.name}
												</ListboxOption>
											))}
										</ListboxOptions>
									</Transition>
								</div>
							)}
						</Listbox>

						{/* Show Formik error if touched + has error */}
						{meta.touched && meta.error && (
							<div className="text-red-500 text-xs">
								<ErrorMessage name={name} />
							</div>
						)}
					</div>
				);
			}}
		</Field>
	);
};
